import { MouseEvent, MutableRefObject, useRef, useState } from 'react'
import { animateDetailsHeight } from 'lib/helpers/shared.helpers'

interface IuseSydExpandableInformationDetails {
  contentRef: MutableRefObject<HTMLDivElement | null>
  detailsRef: MutableRefObject<HTMLDetailsElement | null>
  isExpanding: boolean
  open: boolean
  summaryRef: MutableRefObject<HTMLElement | null>
  toggleOpen: (e: MouseEvent<HTMLButtonElement>) => void
}

const useSydExpandableInformationDetails =
  (): IuseSydExpandableInformationDetails => {
    const [open, setOpen] = useState(false)
    const [isExpanding, setIsExpanding] = useState(false)
    const [isClosing, setIsClosing] = useState(false)

    const detailsRef = useRef<HTMLDetailsElement | null>(null)
    const summaryRef = useRef<HTMLElement | null>(null)
    const contentRef = useRef<HTMLDivElement | null>(null)
    const animationRef = useRef<Animation | null>(null)

    const toggleOpen = (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      if (!detailsRef.current) return
      detailsRef.current.style.overflow = 'hidden'
      if (isClosing || !open) {
        openDetails()
      } else if (isExpanding || open) {
        closeDetails()
      }
    }

    const closeDetails = () => {
      if (!detailsRef.current || !summaryRef.current) return
      setIsClosing(true)
      const endHeight = `${summaryRef.current.offsetHeight}px`

      animateDetailsHeight(animationRef, detailsRef, endHeight)

      if (animationRef.current) {
        animationRef.current.onfinish = () => onAnimationFinish(false)
        animationRef.current.oncancel = () => setIsClosing(false)
      }
    }

    const openDetails = () => {
      detailsRef.current!.style.height = `${detailsRef.current?.offsetHeight}px`
      setOpen(true)
      window.requestAnimationFrame(() => expandDetails())
    }

    const expandDetails = () => {
      if (!detailsRef.current || !summaryRef.current || !contentRef.current)
        return
      setIsExpanding(true)
      const endHeight = `${
        summaryRef.current.offsetHeight + contentRef.current.offsetHeight
      }px`

      animateDetailsHeight(animationRef, detailsRef, endHeight)

      if (animationRef.current) {
        animationRef.current.onfinish = () => onAnimationFinish(true)
        animationRef.current.oncancel = () => setIsExpanding(false)
      }
    }

    const onAnimationFinish = (isOpen: boolean) => {
      if (!detailsRef.current) return
      setOpen(isOpen)
      animationRef.current = null
      setIsClosing(false)
      setIsExpanding(false)
      detailsRef.current.style.height = ''
      detailsRef.current.style.overflow = ''
    }

    return {
      contentRef,
      detailsRef,
      isExpanding,
      open,
      summaryRef,
      toggleOpen,
    }
  }

export default useSydExpandableInformationDetails
